import {
  Cross2Icon,
  ExternalLinkIcon,
  InfoCircledIcon,
} from '@radix-ui/react-icons';
import {
  Badge,
  Box,
  Button,
  Card,
  Flex,
  Grid,
  Heading,
  IconButton,
  Popover,
  Skeleton,
  Text,
} from '@radix-ui/themes';
import { verboseDebug } from 'classes/helpers/debug.helper';
import { NotifyHelper } from 'classes/helpers/notify.helper';
import { PresetTrainingHelper } from 'classes/helpers/preset-training.helper';
import { HELP_URLS } from 'classes/helpers/url.helper';
import { ErrorBoundary } from 'components/common/error-boundary';
import { TrainingDataTable } from 'components/common/training-data-table';
import { MachineCameraStreamViewer } from 'components/machine/camera-stream-viewer';
import { StreamingProvider } from 'components/machine/camera-stream-viewer/context';
import { PresetTrainingContext } from 'components/machine/dialogs/preset-training/context';
import { PresetTrainingBreaks } from 'components/machine/dialogs/preset-training/controls/breaks';
import { PresetSelector } from 'components/machine/dialogs/preset-training/controls/preset-selector';
import { PresetTrainingStatus } from 'components/machine/dialogs/preset-training/controls/status';
import { PresetTrainingPlateView } from 'components/machine/dialogs/preset-training/plate';
import { AimingContext } from 'contexts/aiming.context';
import { FiringContext } from 'contexts/firing/context';
import { MachineContext } from 'contexts/machine.context';
import { TrainingContext } from 'contexts/training.context';
import { addSeconds, isBefore, isFuture, parseISO } from 'date-fns';
import { IgnoreAutoFireMode } from 'enums/firing';
import { PTStep, ProgressDirection } from 'enums/training.enums';
import useLatest from 'hooks/useLatestRef';
import { t } from 'i18next';
import { IVerboseComponent } from 'interfaces/i-components';
import { IPastIteration } from 'interfaces/i-preset-training';
import { AimingHelper } from 'lib_ts/classes/aiming.helper';
import { ArrayHelper } from 'lib_ts/classes/array.helper';
import { BallHelper } from 'lib_ts/classes/ball.helper';
import { EllipseHelper } from 'lib_ts/classes/ellipse.helper';
import { FT_TO_INCHES } from 'lib_ts/classes/math.utilities';
import { MiscHelper } from 'lib_ts/classes/misc.helper';
import { ShotRejectionHelper } from 'lib_ts/classes/shot-rejection.helper';
import { StringHelper } from 'lib_ts/classes/string.helper';
import { TrainingHelper } from 'lib_ts/classes/training.helper';
import { TrajHelper } from 'lib_ts/classes/trajectory.helper';
import { UserRole } from 'lib_ts/enums/auth.enums';
import { CameraSource } from 'lib_ts/enums/machine-msg.enum';
import { FireOption } from 'lib_ts/enums/machine.enums';
import { BuildPriority } from 'lib_ts/enums/pitches.enums';
import { RADIX } from 'lib_ts/enums/radix-ui';
import { getConfidenceCriteria } from 'lib_ts/enums/shot-rejection.enums';
import {
  PRECISE_PT_MODES,
  PresetTrainingMode,
  TrackingDevice,
} from 'lib_ts/enums/training.enums';
import { HasId } from 'lib_ts/interfaces/common/i-extensions';
import { Nullable } from 'lib_ts/interfaces/common/i-generics';
import {
  DEFAULT_PLATE,
  IBallState,
  IClosedLoopBuildChars,
  ITrajectory,
  ITrajektRefBreak,
} from 'lib_ts/interfaces/pitches';
import { ITrainingPitch } from 'lib_ts/interfaces/pitches/i-pitch';
import { IMachineShot } from 'lib_ts/interfaces/training/i-machine-shot';
import { IPresetNotes } from 'lib_ts/interfaces/training/i-trained-machine-state';
import {
  IItrError,
  IItrLog,
  ITrainingLog,
} from 'lib_ts/interfaces/training/i-training-log';
import { clamp, clone, compact, first, maxBy, mean } from 'lodash';
import {
  FC,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { ShotsService } from 'services/shots.service';
import { StateTransformService } from 'services/state-transform.service';
import { TrainedMsService } from 'services/trained-machine-states.service';
import { TrainingLogsService } from 'services/training-logs.service';
import { useAuthStore } from 'stores/use-auth-store';
import useMachineStatusStore from 'stores/use-machine-status-store';
import { useTrainedMsStore } from 'stores/use-trained-ms-store';

const ROTATION_STEP_SIZE = 0.75;

const USE_MSE_SCORE = true;

const SUMMARY_FN = MiscHelper.getMedianObject;

export const CardPadding0 = (props: { children: ReactNode }) => (
  <Card style={{ padding: '0' }}>{props.children}</Card>
);

interface IProps {
  pitches: ITrainingPitch[];

  // callback when training of every pitch is complete
  onFinish?: () => void;

  // mainly for RT to keep track of trained mss
  updateTrainingPitch?: (payload: HasId & Partial<ITrainingPitch>) => void;
}

const HeadingInfoPopover = (props: { children: ReactNode }) => (
  <Popover.Root>
    <Popover.Trigger>
      <InfoCircledIcon />
    </Popover.Trigger>
    <Popover.Content asChild>
      <Card className="no-background" style={{ padding: 0 }}>
        {props.children}
      </Card>
    </Popover.Content>
  </Popover.Root>
);

const stDev = (array: Nullable<number>[]) => {
  const safeArray = array.filter((m) => m !== null) as number[];

  if (safeArray.length < 2) {
    return 0;
  }

  const avg = mean(safeArray);
  const variance = mean(safeArray.map((x) => Math.pow(x - avg, 2)));
  return Math.sqrt(variance);
};

export const PresetTrainingControls: FC<IProps & IVerboseComponent> = (
  props
) => {
  const current = useAuthStore((s) => s.current);

  const s2mReady = useMachineStatusStore((s) => s.s2mReady);
  const fireStateDates = useMachineStatusStore((s) => s.fireStateDates);

  const {
    autoFire,
    checkConfidence,
    lastMSHash,
    machine,
    activeModel,
    setFireOption,
  } = useContext(MachineContext);

  const {
    finalMsg,
    liveView,
    pitchIndex,
    shots: trainingShots,
    startDate,
    setStartDate,
    setShots: setTrainingShots,
    setLiveView,
    setPitchIndex,
  } = useContext(TrainingContext);

  const latestStartDate = useLatest(startDate);

  const {
    active: activeMode,
    gradient,
    iAdjustment,
    lastTrainedMs,
    showFailure,
    step,
    changeMode,
    getNextStep,
    setIAdjustment,
    setLastTrainedMs,
    setShowFailure,
    setStep,
  } = useContext(PresetTrainingContext);

  const { pitchTraining, aimForTraining, setAndSendForTraining } =
    useContext(AimingContext);
  const latestSSTraining = useLatest(setAndSendForTraining);

  const { ignoreAf, lastFire, safeSetIgnoreAf, setLoadingUntil } =
    useContext(FiringContext);

  // * STATE VARS

  // for allowing auto-send on mount but disabling auto-send later if the machine was lowered during training
  const [init, setInit] = useState(false);

  const [log, setLog] = useState<ITrainingLog>();
  const latestLog = useLatest(log);

  // for jumping in and out of quick mode (e.g. when precision modes fail to get to spec)
  const [previousMode, setPreviousMode] = useState<PresetTrainingMode>();

  // per pitch
  const [bestIteration, setBestIteration] = useState<IPastIteration>();

  // newest shot from matching query
  const [newestShot, setNewestShot] = useState<IMachineShot>();

  const [pitches, setPitches] = useState<ITrainingPitch[]>([]);

  const [ignoreFiresDate] = useState(new Date());

  // for current iteration only
  const [fireIDs, setFireIDs] = useState<string[]>([]);

  // append fireIDs as the lastFire changes
  useEffect(() => {
    if (!lastFire) {
      // nothing to append
      return;
    }

    if (isBefore(new Date(lastFire._created), ignoreFiresDate)) {
      // fire occurred earlier
      return;
    }

    // append it to the end of the existing
    setFireIDs((prev) => ArrayHelper.unique([...prev, lastFire._id]));
  }, [lastFire, ignoreFiresDate]);

  // todo: use activePitch for other effects to avoid excessive triggering when both pitchIndex and pitches change
  const activePitch = useMemo<ITrainingPitch | undefined>(
    () => pitches[pitchIndex],
    [pitches, pitchIndex]
  );

  // manually triggers update matches even if nothing else changed
  const [updateMatchesKey, setUpdateMatchesKey] = useState(Date.now());

  // if user has already seen the failure callout and has clicked on continue (or allowed it to timeout)
  const [ignoreFailure, setIgnoreFailure] = useState(false);

  // ! this is a hack to reset the streaming context; deprecate this once machines know how to resume streaming on their own
  const [liveKey, setLiveKey] = useState(Date.now());

  // * MEMO VALUES

  // newest shot from matching query that could be used for aiming (i.e. good shots only)
  const lastAimingShot = useMemo(() => {
    const msHash = pitchTraining?._mx_ms.matching_hash;

    if (!msHash) {
      return undefined;
    }

    const relevantShots = trainingShots.filter(
      (m) => m.target_ms.matching_hash === msHash
    );

    return maxBy(relevantShots, (m) => parseISO(m._created).getTime());
  }, [pitchTraining?._mx_ms.matching_hash, trainingShots]);

  const isLastPitch = useMemo(
    () => pitchIndex === pitches.length - 1,
    [pitchIndex, pitches]
  );

  // * LATEST REFS
  const latestActiveMode = useLatest(activeMode);
  const latestAimForTraining = useLatest(aimForTraining);
  const latestCheckConfidence = useLatest(checkConfidence);
  const latestCurrentRole = useLatest(current.role);
  const latestGetNextStep = useLatest(getNextStep);
  const latestIAdjustment = useLatest(iAdjustment);
  const latestIgnoreFailure = useLatest(ignoreFailure);
  const latestInit = useLatest(init);
  const latestIsLastPitch = useLatest(isLastPitch);
  const latestLastMsHash = useLatest(lastMSHash);
  const latestMaxAdjustments = useLatest(activeMode.spec.maxAdjustments);
  const latestOnFinish = useLatest(props.onFinish);
  const latestPitchPriority = useLatest(pitchTraining?.priority);
  const latestRefPitches = useLatest(props.pitches);
  const latestSafeSetIgnoreAf = useLatest(safeSetIgnoreAf);
  const latestSetFireOption = useLatest(setFireOption);
  const latestUpdateTrainingPitch = useLatest(props.updateTrainingPitch);

  // * CALLBACKS
  const changePitch = useCallback(
    async (delta: ProgressDirection) => {
      verboseDebug(props, 'change pitch triggered', { delta, previousMode });

      if (previousMode) {
        // go back to the previously active preset, will be consumed in the setState below
        changeMode(previousMode);
      }

      setLoadingUntil(addSeconds(new Date(), 2));
      setPitchIndex((prev) => clamp(prev + delta, 0, pitches.length - 1));
    },
    [previousMode, pitches]
  );

  const latestChangePitch = useLatest(changePitch);

  const onFailureContinue = useCallback(() => {
    // prevents repeatedly showing the failure callout after it's been dismissed
    setIgnoreFailure(true);

    // hide the callout
    setShowFailure(false);

    // basically to trigger getNextStep again
    setTimeout(() => {
      verboseDebug(
        props,
        'spec failure > continue > triggering update matches'
      );
      setUpdateMatchesKey(Date.now());
    }, 100);
  }, [isLastPitch, pitchTraining, activeMode]);

  const applyBestIteration = useCallback(
    async (config: { refPitch: ITrainingPitch; iteration: IPastIteration }) => {
      verboseDebug(props, 'apply best iteration triggered', config);

      const { refPitch, iteration } = config;

      if (iteration.index > 1 && activeMode.mode !== PresetTrainingMode.Quick) {
        NotifyHelper.info({
          message_md: `Proceeding with results from the best iteration (${iteration.index}).`,
        });
      }

      // upsert a trained ms record
      const safeSpec = clone(activeMode.spec);
      delete safeSpec.minutesPerPitch;

      const preset: IPresetNotes = {
        mode: iteration.mode,
        spec: iteration.spec,
        iteration: iteration.index,
      };

      // assume we've fired enough shots to Quick Train if best iteration is still out of spec
      if (iteration.withinSpec) {
        iteration.ms.precision_trained = true;
      }

      if (!iteration.withinSpec) {
        preset.mode = PresetTrainingMode.Quick;
      }

      const safePitch = clone(refPitch);
      safePitch._mx_ms = iteration.ms;

      latestUpdateTrainingPitch.current?.({
        _id: safePitch._id,
        _mx_ms: iteration.ms,
      });

      // for canvases to draw the shots from the (possibly past) iteration
      setTrainingShots(iteration.matches);

      // keep the updated pitch in state (e.g. if we need to manually resend after lowered)
      setPitches((prev) =>
        prev.map((p) => (p._id === safePitch._id ? safePitch : p))
      );

      // restore the fireIDs recorded while on this iteration from before
      setFireIDs(iteration.fireIDs);
    },
    [
      activeMode.spec,
      machine.machineID,
      machine.ball_type,
      machine.releaseFt,
      setTrainingShots, // shouldn't change
      setPitches, // shouldn't change
    ]
  );

  const latestApplyBestIteration = useLatest(applyBestIteration);

  const iterate = useCallback(
    async (config: {
      pitch: ITrainingPitch;
      aimedTraj: ITrajectory;
      shots: IMachineShot[];
      adjustment: number;
      // we need to kick the UI out of non-firing step (e.g. so user can fire again to overcome whatever failure was encountered)
      onFailure: (error: string) => void;
    }) => {
      verboseDebug(props, 'iterate triggered', config);

      const { pitch, shots, onFailure } = config;

      if (!pitch) {
        NotifyHelper.error({
          message_md: 'Cannot proceed without a pitch.',
        });
        onFailure('Cannot proceed without a pitch.');
        return;
      }

      // run an iteration of the closed loop logic, if we haven't run out of attempts
      const { bs: targetBs } = pitch;

      if (!targetBs) {
        NotifyHelper.error({
          message_md: 'Cannot proceed without a target ball state.',
        });
        onFailure('Cannot proceed without a target ball state.');
        return;
      }

      const priority = pitch.priority ?? BuildPriority.Spins;

      const shotsWithBS = shots.filter((s) => s.bs);

      const summaryBs =
        shotsWithBS.length > 0
          ? (SUMMARY_FN(
              shotsWithBS.map((s) => s.bs as IBallState)
            ) as IBallState)
          : undefined;

      if (priority === BuildPriority.Spins) {
        if (!summaryBs) {
          NotifyHelper.error({
            message_md: 'Cannot proceed without a summary ball state.',
          });
          onFailure('Cannot proceed without a summary ball state.');
          return;
        }
      }

      const spinPrioChars = () => {
        const o: IClosedLoopBuildChars = {
          machineID: machine.machineID,
          mongo_id: pitch._id,
          use_gradient: gradient,
          ms: pitch._mx_ms,
          traj: config.aimedTraj,
          seams: pitch.seams,
          target_bs: targetBs,
          actual_bs: summaryBs ?? targetBs,
          priority: priority,
        };

        return o;
      };

      const summaryBreaks =
        shots.length > 0
          ? (SUMMARY_FN(
              shots.map((s) => TrajHelper.getBreaksFromShot(s))
            ) as ITrajektRefBreak)
          : undefined;

      const shotsWithTraj = shots.filter((s) => s.traj);
      const summaryTraj =
        shotsWithTraj.length > 0
          ? (SUMMARY_FN(compact(shots.map((s) => s.traj))) as ITrajectory)
          : undefined;

      const pitchBreaks = BallHelper.getSafePitchBreaks(pitch);

      if (priority === BuildPriority.Breaks) {
        if (!pitchBreaks) {
          NotifyHelper.error({
            message_md: 'Cannot proceed without target breaks from pitch.',
          });
          onFailure('Cannot proceed without target breaks from pitch.');
          return;
        }

        if (!summaryBreaks) {
          NotifyHelper.error({
            message_md: 'Cannot proceed without a summary break.',
          });
          onFailure('Cannot proceed without a summary break.');
          return;
        }

        if (!summaryTraj) {
          NotifyHelper.error({
            message_md: 'Cannot proceed without a summary traj.',
          });
          onFailure('Cannot proceed without a summary traj.');
          return;
        }
      }

      const breakPrioChars = () => {
        const o: IClosedLoopBuildChars = {
          machineID: machine.machineID,
          mongo_id: pitch._id,
          use_gradient: gradient,
          ms: pitch._mx_ms,
          seams: pitch.seams,
          traj: summaryTraj ?? config.aimedTraj,
          target_bs: targetBs,
          // actual_bs can fall back to target_bs in break priority, but a BS does need to be provided here.
          // TODO: handle this in Python, and make the actual_bs optional.
          actual_bs: summaryBs ?? targetBs,
          actual_mvmt: summaryBreaks
            ? {
                break_x_ft: summaryBreaks.xInches / FT_TO_INCHES,
                break_z_ft: summaryBreaks.zInches / FT_TO_INCHES,
              }
            : undefined,
          target_mvmt: pitchBreaks
            ? {
                break_x_ft: pitchBreaks.xInches / FT_TO_INCHES,
                break_z_ft: pitchBreaks.zInches / FT_TO_INCHES,
              }
            : undefined,
          priority: priority,
        };

        return o;
      };

      const chars =
        priority === BuildPriority.Breaks ? breakPrioChars() : spinPrioChars();

      const stepSizeData = PresetTrainingHelper.getStepSize({
        target: pitch,
        shots: shots,
      });

      NotifyHelper.success({
        message_md: `Attempting optimization of pitch ${pitch.name}...`,
        delay_ms: 3_000,
      });

      const result = first(
        await StateTransformService.getInstance().buildManyClosedLoop({
          machineID: machine.machineID,
          pitches: [chars],
          stepSize: stepSizeData.stepSize,
          notifyError: false,
        })
      );

      verboseDebug(
        props,
        `iterate > adjustment ${config.adjustment} > ${pitch.name}`,
        result
      );

      if (!result) {
        // force things to proceed for this pitch as if quick training
        const keyword =
          pitch.priority === BuildPriority.Breaks ? 'break' : 'spin';

        const msg = `Your current model does not support ${keyword} data. Precision training for this pitch will be available if you update your model to support ${keyword}.`;

        NotifyHelper.warning({ message_md: msg });

        // so we don't get stuck even if we fail
        setStep(PTStep.Sending);

        setTimeout(() => {
          verboseDebug(props, 'iterate > failure > triggering update matches');
          setUpdateMatchesKey(Date.now());
        }, 100);
        return;
      }

      const output = clone(pitch);
      output._mx_ms = result.ms;
      return output;
    },
    [machine.machineID, gradient]
  );

  // b/c this is needed by update matches
  const latestIterate = useLatest(iterate);

  // * EFFECTS
  // parent component may be building the pitches, so they may not be ready at mount
  useEffect(() => {
    verboseDebug(props, 'setPitches > props.pitches changed', {
      fromProps: props.pitches,
    });

    setPitches(
      props.pitches
        .map((p) => clone(p))
        .sort((a, b) => StringHelper.safeSort(a.name, b.name))
    );
  }, [props.pitches]);

  // confidence checks if necessary, when latestShot changes
  useEffect(() => {
    verboseDebug(props, 'confidence check > new shot', newestShot);

    if (!newestShot) {
      verboseDebug(props, 'confidence check > skipped');
      return;
    }

    const { reject } = ShotRejectionHelper.filterShot({
      shot: newestShot,
      criteria: getConfidenceCriteria(latestPitchPriority.current),
    });

    if (!reject) {
      verboseDebug(props, 'confidence check > acceptable');
      return;
    }

    // everyone sees the following toast WHILE NOT skipping tracking device validation
    if (latestCheckConfidence.current) {
      verboseDebug(props, 'confidence check > low confidence detected');

      const route = newestShot.rapsodo_shot_id
        ? HELP_URLS.RAPSODO_ERRORS
        : newestShot.trackman_pitch_id
          ? HELP_URLS.TRACKMAN_ERRORS
          : undefined;

      NotifyHelper.warning({
        message_md: `Low confidence detected.`,
        buttons: [
          {
            label: 'common.read-more',
            invisible: !route,
            onClick: () => window.open(t('common.intercom-url') + route),
          },
          {
            label: 'common.skip-validation',
            dismissAfterClick: true,
            onClick: () =>
              latestSetFireOption.current(FireOption.SkipConfidenceCheck, true),
          },
        ],
      });
      return;
    }

    // only super admins see the following toast WHILE skipping tracking device validation
    if (latestCurrentRole.current === UserRole.admin) {
      verboseDebug(props, 'confidence check > low confidence detected > admin');

      NotifyHelper.warning({
        isSuperAdminOnly: true,
        message_md:
          'Low confidence detected. This warning will continue to be shown to you as a Super Admin.',
        buttons: [
          {
            label: 'common.enable-validation',
            dismissAfterClick: true,
            onClick: () =>
              latestSetFireOption.current(
                FireOption.SkipConfidenceCheck,
                false
              ),
          },
        ],
      });
      return;
    }
  }, [newestShot?._id]);

  const firing = useMemo(() => {
    return isFuture(fireStateDates.awaitFiringUntil);
  }, [fireStateDates.awaitFiringUntil]);

  // when final msg is ready and firing is complete, check if any warnings about missed detections should show
  useEffect(() => {
    if (firing) {
      console.log(
        `${props.identifier}: Firing still in progress, skipping final msg handling. Final Msg:`,
        finalMsg
      );
      return;
    }

    if (!finalMsg) {
      console.log(
        `${props.identifier}: No final msg, skipping final msg handling`
      );
      return;
    }

    try {
      if (finalMsg.success) {
        verboseDebug(
          props,
          'training response > success > triggering update matches'
        );

        const ts = Date.now();

        console.log(
          `${props.identifier}: Setting update matches key to ${new Date(
            ts
          ).toISOString()}`
        );

        setUpdateMatchesKey(ts);
        return;
      }

      if (finalMsg.timeout) {
        verboseDebug(
          props,
          'training response > failure > timeout detected',
          finalMsg
        );
        const msg = (() => {
          switch (finalMsg.timeout.missing) {
            case 'shot': {
              return t('tr.training-timeout-no-shot-msg', {
                sec: finalMsg.timeout.delay_s,
              });
            }

            case 'MSBS':
            case TrackingDevice.RapsodoV3PRO:
            case TrackingDevice.TrackmanB1:
            case TrackingDevice.TrajektVision:
            default: {
              return t('tr.training-timeout-missing-msg', {
                sec: finalMsg.timeout.delay_s,
                missing: finalMsg.timeout.missing,
              });
            }
          }
        })();

        if (!msg) {
          return;
        }

        NotifyHelper.warning({
          message_md: msg,
          inbox: true,
          buttons: [
            {
              label: 'common.read-more',
              onClick: () =>
                window.open(
                  t('common.intercom-url') + HELP_URLS.RAPSODO_ERRORS
                ),
            },
          ],
        });

        return;
      }

      // unknown/miscellaneous failure to train
      verboseDebug(
        props,
        'training response > failure > unknown error',
        finalMsg
      );

      NotifyHelper.warning({
        message_md: finalMsg.message ?? 'Failed to train (unknown error).',
        inbox: true,
        buttons: [
          {
            label: 'common.read-more',
            onClick: () =>
              window.open(t('common.intercom-url') + HELP_URLS.RAPSODO_ERRORS),
          },
        ],
      });
    } catch (e) {
      console.error(e);
    }
  }, [firing, finalMsg]);

  // update matches > next step and iterate
  useEffect(() => {
    // we need to kick the UI out of non-firing step (e.g. so user can fire again to overcome whatever failure was encountered)
    const returnToFiringCallback = (error: string) => {
      console.log('** returnToFiringCallback:', error);
      verboseDebug(props, 'update matches > error > return to firing', error);
      setLoadingUntil(new Date());
      setStep(PTStep.Firing);
    };

    const callback = async () => {
      if (!pitchTraining) {
        returnToFiringCallback(
          `Training pitch not defined in aiming context for index ${pitchIndex}`
        );
        return;
      }

      if (!pitchTraining._mx_ms) {
        returnToFiringCallback(
          `Invalid pitch (no _mx_ms) for ${pitchTraining.name}`
        );
        return;
      }

      const { matching_hash } = pitchTraining._mx_ms;

      if (!matching_hash) {
        returnToFiringCallback(
          `Invalid pitch (no matching hash) for ${pitchTraining.name}`
        );
        return;
      }

      // pick some time in the future that's reasonable for this task
      setLoadingUntil(addSeconds(new Date(), 5));

      const matches = !latestStartDate.current
        ? [] // always have no matches before startDate is set
        : (
            await ShotsService.getInstance().getMatches({
              matching_hashes: [matching_hash],
              newerThan: latestStartDate.current.toISOString(),
              machineID: machine.machineID,
              ball_type: machine.ball_type,
              // true allows fetching of new low confidence shots to use for warnings (but not aiming)
              includeLowConfidence: true,
              includeHitterPresent: false,
            })
          )[matching_hash];

      if (!matches) {
        // shouldn't trigger
        returnToFiringCallback(`No matches for ${pitchTraining.name}`);
        return;
      }

      // * not in an effect since we don't keep track of non-aiming shots in any state variable
      setNewestShot((prev) => {
        const next = maxBy(matches, (m) => parseISO(m._created).getTime());
        return prev?._id === next?._id ? prev : next;
      });

      // might be changed later if we need to rebuild via closed loop
      const aimed = latestAimForTraining.current({
        aimingShots: matches,
        stepSize: ROTATION_STEP_SIZE,
      });

      if (!aimed) {
        returnToFiringCallback(
          `Failed to rotate ${pitchTraining.name} to middle`
        );
        return;
      }

      setTrainingShots(aimed.usingShots);

      const nextStepResult = latestGetNextStep.current({
        target: pitchTraining,
        shots: aimed.usingShots,
        iAdjustment: latestIAdjustment.current,
      });

      const { nextStep, reason, groupFilterResult, showFailure } =
        nextStepResult;

      verboseDebug(props, `update matches > got next step > ${nextStep}`, {
        result: nextStepResult,
        aimedMs: aimed.pitch._mx_ms,
        preset: activeMode,
        lastAimingShot: lastAimingShot,
      });

      setStep(nextStep);

      if (!latestIgnoreFailure.current) {
        setShowFailure(showFailure); // i.e. show the callout to retrain or continue
      }

      // * start of iterating and next step logic

      if (
        latestLog.current &&
        [PTStep.Complete, PTStep.Iterate].includes(nextStep)
      ) {
        // summarize error and stdev for shots
        // * error is taken as measured minus target (from docs)
        const firstShot = first(aimed.usingShots);

        if (firstShot) {
          const alignedShots = EllipseHelper.getRotatedShots({
            ms: firstShot.target_ms,
            shots: aimed.usingShots,
            releaseFt: firstShot.target_ms.py,
          });

          const shotErrors = alignedShots.map((s) => {
            const shotPlate = TrajHelper.getPlateLoc(s.traj);

            const shotBreaks =
              aimed.pitch.priority === BuildPriority.Breaks
                ? TrajHelper.getBreaksFromShot(s)
                : undefined;

            console.debug({
              event: 'preparing shotErrors',
              shotTraj: s.traj,
              aimedTraj: aimed.pitch.traj,
              pitchTraj: pitchTraining.traj,
            });

            const o: IItrError = {
              vMph:
                TrajHelper.getSpeedFromTraj(s.traj) -
                TrajHelper.getSpeedFromTraj(pitchTraining.traj),

              // ? Adam Turner asked for this, put it back to aimed.plate if anything bad happens
              plateXIn:
                (shotPlate.plate_x - DEFAULT_PLATE.plate_x) * FT_TO_INCHES,
              plateZIn:
                (shotPlate.plate_z - DEFAULT_PLATE.plate_z) * FT_TO_INCHES,

              breakXIn:
                shotBreaks && pitchTraining.breaks
                  ? shotBreaks.xInches - pitchTraining.breaks.xInches
                  : null,
              breakZIn:
                shotBreaks && pitchTraining.breaks
                  ? shotBreaks.zInches - pitchTraining.breaks.zInches
                  : null,

              wxRpm: s.bs ? s.bs.wx - pitchTraining.bs.wx : null,
              wyRpm: s.bs ? s.bs.wy - pitchTraining.bs.wy : null,
              wzRpm: s.bs ? s.bs.wz - pitchTraining.bs.wz : null,
            };

            return o;
          });

          const shotSpeeds = alignedShots.map((s) =>
            TrajHelper.getSpeedFromTraj(s.traj)
          );

          const shotPlates = alignedShots.map((s) =>
            TrajHelper.getPlateLoc(s.traj)
          );

          const shotBreaks = alignedShots.map((s) =>
            TrajHelper.getBreaksFromShot(s)
          );

          // append this iteration to the log
          const nextItrLog: IItrLog = {
            index: latestIAdjustment.current,
            reason: reason,
            preset: {
              mode: latestActiveMode.current.mode,
              spec: latestActiveMode.current.spec,
            },
            meanError: {
              vMph: mean(shotErrors.map((m) => m.vMph)),

              plateXIn: mean(shotErrors.map((m) => m.plateXIn)),
              plateZIn: mean(shotErrors.map((m) => m.plateZIn)),

              breakXIn: mean(shotErrors.map((m) => m.breakXIn)),
              breakZIn: mean(shotErrors.map((m) => m.breakZIn)),

              wxRpm: mean(shotErrors.map((m) => m.wxRpm)),
              wyRpm: mean(shotErrors.map((m) => m.wyRpm)),
              wzRpm: mean(shotErrors.map((m) => m.wzRpm)),
            },
            stDev: {
              vMph: stDev(shotSpeeds),

              plateXIn: stDev(shotPlates.map((m) => m.plate_x)) * FT_TO_INCHES,
              plateZIn: stDev(shotPlates.map((m) => m.plate_z)) * FT_TO_INCHES,

              breakXIn: stDev(shotBreaks.map((m) => m.xInches)),
              breakZIn: stDev(shotBreaks.map((m) => m.zInches)),

              wxRpm: stDev(alignedShots.map((m) => m.bs?.wx ?? null)),
              wyRpm: stDev(alignedShots.map((m) => m.bs?.wy ?? null)),
              wzRpm: stDev(alignedShots.map((m) => m.bs?.wz ?? null)),
            },
            shotIDs: alignedShots.map((m) => m._id),

            // ? use fireIDs for all fire events created, not just the ones where detection was successful (shots only exist if detection worked)
            fireIDs: fireIDs,
          };

          console.debug(
            `Appending iteration ${nextItrLog.index} to log iterations:`,
            nextItrLog
          );

          // update the record in the database
          await TrainingLogsService.getInstance()
            .updateOne({
              _id: latestLog.current._id,
              iterations: [...latestLog.current.iterations, nextItrLog],
            })
            .then((result) => setLog(result));
        }
      }

      switch (nextStep) {
        case PTStep.Complete: {
          if (!lastAimingShot) {
            // shouldn't trigger
            verboseDebug(props, 'update matches > no last aiming shot');
            break;
          }

          verboseDebug(
            props,
            `update matches > complete > ${pitchTraining.name}`
          );

          // upsert a trained ms record
          const safeSpec = clone(activeMode.spec);
          delete safeSpec.minutesPerPitch;

          const precisionTrained =
            !groupFilterResult.reject && activeMode.canPrecisionTrain;

          // active mode is ignored if completed without success (e.g. ran out of iterations)
          const safeMode = precisionTrained
            ? activeMode.mode
            : PresetTrainingMode.Quick;

          if (PRECISE_PT_MODES.includes(safeMode)) {
            aimed.pitch._mx_ms.precision_trained = true;
          }

          const aimingTraj = AimingHelper.getAimingTraj({
            refMs: aimed.pitch._mx_ms,
            shots: aimed.usingShots,
            releaseFt: machine.releaseFt,
          });

          // ! remove this after debugging
          console.debug({
            event: 'training complete > compare trajs',
            aimingTraj: aimingTraj,
            usingTraj: aimed.usingTraj,
          });

          const contShotErrors = aimingTraj
            ? // seed the list w/ errors from our training shots
              compact(
                TrainingHelper.alignTrainingShots(
                  aimed.pitch._mx_ms,
                  machine.releaseFt,
                  aimingTraj,
                  aimed.usingShots
                ).map((s) => TrainingHelper.getContShotError(s))
              )
            : // shouldn't trigger
              null;

          // don't do this via the TMS context to avoid flickering things in the background
          const upsertedTms = await TrainedMsService.getInstance().upsertOne({
            machineID: machine.machineID,
            ball_type: machine.ball_type,
            bs_hash: latestRefPitches.current.find(
              (p) => p._id === pitchTraining._id
            )?.bs_hash,
            ms: aimed.pitch._mx_ms,
            preset: {
              mode: safeMode,
              spec: safeSpec,
              iteration: latestIAdjustment.current,
            },
            shotIDs: aimed.usingShots.map((s) => s._id),
            aimingTraj: aimingTraj,
            contShotErrors: contShotErrors,

            // note the log for reference later
            currLogID: latestLog.current?._id ?? null,

            // reset CT stuff
            contAimingTraj: null,
            contMs: null,
            contShotIDs: null,
            last_adjustments: null,

            // reset misc stuff
            discardedShotIDs: null,
            skipRefresh: false,
            deleted: false,
          });

          verboseDebug(
            props,
            'update matches > complete > upsert TMS',
            upsertedTms
          );
          setLastTrainedMs(upsertedTms);

          if (!upsertedTms) {
            // shouldn't trigger, but upsert failed
            break;
          }

          // ensure the tms ID is attached to the log record
          if (latestLog.current) {
            await TrainingLogsService.getInstance()
              .updateOne({
                _id: latestLog.current._id,
                tmsID: upsertedTms._id,
                precisionTrained: precisionTrained,
                completed: true,
              })
              .then((result) => setLog(result));
          }

          // in case parent component needs to know the ms was updated
          props.updateTrainingPitch?.({
            _id: pitchTraining._id,
            _mx_ms: upsertedTms.ms,
          });

          // attach the updated TMS to the pitch in case we care about it later
          setPitches((prev) =>
            prev.map((p) =>
              p._id === pitchTraining._id
                ? { ...p, _mx_ms: upsertedTms.ms } // update the mixin value
                : p
            )
          );
          break;
        }

        case PTStep.Iterate: {
          verboseDebug(
            props,
            `update matches > iterate > ${pitchTraining.name}`
          );

          const score = PresetTrainingHelper.getIterationScore({
            spec: activeMode.spec,
            target: aimed.pitch,
            shots: aimed.usingShots,
          });

          const safeSpec = clone(activeMode.spec);
          delete safeSpec.minutesPerPitch;

          const nextIAdjustment = latestIAdjustment.current + 1;

          //  current iteration; kept if its score is lowest
          const currItr: IPastIteration = {
            index: nextIAdjustment,
            ms: aimed.pitch._mx_ms,
            matches: aimed.usingShots,
            worstScore: USE_MSE_SCORE ? score.byMse : score.byDelta,
            mode: latestActiveMode.current.mode,
            spec: safeSpec,
            withinSpec: !groupFilterResult.reject,
            fireIDs: fireIDs,
          };

          // compare to itself (i.e. doesn't matter) if this is the first iteration
          const prevItr = bestIteration ?? currItr;

          const bestItr =
            currItr.worstScore < prevItr.worstScore ? currItr : prevItr;

          setBestIteration(bestItr);

          verboseDebug(
            props,
            `update matches > iterate > keeping best iteration`,
            bestItr
          );

          // * DO NOT MOVE THIS EVER - thanks Nitesh
          setIAdjustment(nextIAdjustment);

          verboseDebug(
            props,
            `update matches > iAdjustment incremented > ${nextIAdjustment}`
          );

          if (nextIAdjustment <= latestMaxAdjustments.current) {
            // attempt an adjustment
            const updatedPitch = await latestIterate.current({
              pitch: pitchTraining, // original pitch for target bs
              aimedTraj: aimed.usingTraj, // aimed traj for closed loop logic
              shots: aimed.usingShots,
              adjustment: nextIAdjustment,
              onFailure: returnToFiringCallback,
            });

            // if updatedPitch is undefined, that means iterate failed
            if (updatedPitch) {
              verboseDebug(
                props,
                `update pitches > iterated > ${updatedPitch.name}`,
                updatedPitch
              );

              // in case parent component needs to know the ms was updated
              props.updateTrainingPitch?.({
                _id: updatedPitch._id,
                _mx_ms: updatedPitch._mx_ms,
              });

              // just in case editing the pitch in place doesn't actually "save" the changes for the next iteration
              setPitches((prev) =>
                prev.map((p) =>
                  p._id === updatedPitch._id ? clone(updatedPitch) : p
                )
              );
            }

            // assumption is there can't be matches for this yet since it's a brand new ms
            setTrainingShots([]);
          } else {
            // we're out of iterations so just use the best one we've come across
            verboseDebug(
              props,
              `update matches > apply best iteration > ${pitchTraining.name}`,
              bestItr
            );

            await latestApplyBestIteration.current({
              refPitch: pitchTraining,
              iteration: bestItr,
            });

            // basically to trigger getNextStep again
            setTimeout(() => {
              verboseDebug(
                props,
                'update matches > applied best iteration > triggering update matches'
              );
              setUpdateMatchesKey(Date.now());
            }, 100);
          }
          break;
        }

        default: {
          break;
        }
      }
    };

    // only make a query 1x per group of changes
    const timeout = setTimeout(() => {
      callback();
    }, 500);

    return () => clearTimeout(timeout);
  }, [
    machine.machineID,
    machine.ball_type,
    updateMatchesKey,
    pitchTraining?._mx_ms.matching_hash,
    setNewestShot, // shouldn't change
    setTrainingShots, // shouldn't change
  ]);

  // auto-move to firing after a brief delay for sending and iterating
  useEffect(() => {
    if (![PTStep.Sending, PTStep.Iterate].includes(step)) {
      // ignore irrelevant steps
      return;
    }

    verboseDebug(props, `entered ${step} > return to firing scheduled`);

    const timeout = setTimeout(
      () => {
        // i.e. if temporarily ignoring AF, stop ignoring
        latestSafeSetIgnoreAf.current(IgnoreAutoFireMode.Disabled);

        // move to firing step
        setStep(PTStep.Firing);
      },
      // longer timeout is to prevent premature auto-fire
      step === PTStep.Sending ? 500 : 3_000
    );

    return () => clearTimeout(timeout);
  }, [step]);

  // whenever pitches or pitchIndex changes, update pitchTraining
  useEffect(() => {
    const callback = async () => {
      const activePitch = pitches[pitchIndex];

      if (!activePitch) {
        verboseDebug(props, 'clearing training pitch');
        latestSSTraining.current({
          pitch: undefined,
          sendConfig: undefined,
        });
        return;
      }

      // auto-send on mount; don't auto-send subsequently IF the machine was lowered
      const shouldSend = !latestInit.current || latestLastMsHash.current;

      // safeguard against stale shots (e.g. for prev pitch) being used for aiming
      const relevantShots = shouldSend
        ? trainingShots.filter(
            (s) =>
              s.target_ms.matching_hash === activePitch._mx_ms.matching_hash
          )
        : [];

      verboseDebug(props, 'setting training pitch', {
        activePitch,
        shouldSend,
        relevantShots,
      });

      latestSSTraining.current({
        pitch: activePitch,
        sendConfig:
          s2mReady && shouldSend
            ? {
                skipPreview: true,
                trigger: `${props.identifier} > auto-send`,
                aimingShots: relevantShots,
              }
            : undefined,
      });

      setInit(true);
    };

    // only resend when things have settled
    const timeout = setTimeout(() => {
      callback();
    }, 500);

    return () => clearTimeout(timeout);
  }, [s2mReady, pitchIndex, pitches, trainingShots]);

  // reset whenever pitchIndex changes or explicitly instructed to (e.g. user wants to retrain)
  const resetEverything = () => {
    verboseDebug(props, 'resetting state');
    setStep(PTStep.Waiting);
    setShowFailure(false);
    setIAdjustment(0);
    setPreviousMode(undefined);
    setBestIteration(undefined);
    setTrainingShots([]); // update matches would be triggered but this clears the visualizers until the load finishes
    setIgnoreFailure(false);
  };

  // create a training log whenever pitches changes
  useEffect(() => {
    if (!activePitch?._id) {
      // pitches aren't ready yet
      return;
    }

    const callback = async () => {
      // check if the existing log had no iterations, cleanup if so
      if (latestLog.current && latestLog.current.iterations.length === 0) {
        await TrainingLogsService.getInstance().deleteOne(
          latestLog.current._id
        );
      }

      // if there's an existing tms w/ currLogID, attach it to the log as prevLogID
      const tms = useTrainedMsStore.getState().getTms(activePitch.bs_hash); // ignores any tms marked as deleted

      const payload: Partial<ITrainingLog> = {
        iterations: [],
        precisionTrained: false,
        completed: false,
        machineID: machine.machineID,
        ball_type: machine.ball_type,
        modelID: activeModel?._id,
        modelType: activeModel?.type,
        tracking_device: machine.tracking_device,
        training_threshold: machine.training_threshold,
        bs_hash: activePitch.bs_hash,
        pitchID: activePitch._id,
        pitchName: activePitch.name ?? '',
        pitchPriority: activePitch.priority ?? BuildPriority.Spins,
        breaksTarget:
          activePitch.priority === BuildPriority.Breaks && activePitch.breaks
            ? {
                vMph: TrajHelper.getSpeedFromTraj(activePitch.traj),
                breakXIn: activePitch.breaks.xInches,
                breakZIn: activePitch.breaks.zInches,
              }
            : null,
        spinTarget:
          activePitch.priority === BuildPriority.Spins
            ? {
                vMph: TrajHelper.getSpeedFromTraj(activePitch.traj),
                wXRpm: activePitch.bs.wx,
                wYRpm: activePitch.bs.wy,
                wZRpm: activePitch.bs.wz,
              }
            : null,
        prevLogID: tms?.currLogID ?? null,
        tmsID: tms?._id ?? null,
      };

      // insert a training log to capture whatever happens here
      const createdLog =
        await TrainingLogsService.getInstance().createOne(payload);

      console.debug(`Created training log:`, createdLog);

      setLog(createdLog);
    };

    // prevent multiple creations if many things change
    const timeout = setTimeout(() => {
      callback();
    }, 200);

    return () => {
      clearTimeout(timeout);
    };
  }, [
    machine,
    activeModel,
    activePitch?._id, // avoid re-creating logs when pitches change, but also ensures that the training pitch has loaded first
  ]);

  useEffect(() => {
    resetEverything();
  }, [pitchIndex]);

  // auto-change pitch after a brief delay for completing
  useEffect(() => {
    if (step !== PTStep.Complete) {
      // ignore irrelevant steps
      return;
    }

    if (showFailure) {
      // do not change pitches while waiting on user to retrain vs continue
      verboseDebug(
        props,
        `completed > skip change > waiting for retrain vs continue`
      );
      return;
    }

    if (!autoFire) {
      // do not auto-advance while not auto-firing
      verboseDebug(
        props,
        `completed > skip change > not auto-firing or ignoring auto-fire`,
        { autoFire, ignoreAf }
      );
      return;
    }

    // give time for users to see the complete badge before moving ahead
    const timeout = setTimeout(() => {
      if (latestIsLastPitch.current) {
        verboseDebug(props, `completed > auto-finish > last pitch`);
        latestOnFinish.current?.();
        return;
      }

      verboseDebug(props, `completed > auto-advance > next pitch`);
      latestChangePitch.current(ProgressDirection.Next);
    }, 3_000);

    return () => clearTimeout(timeout);
  }, [step, showFailure, autoFire]);

  // ! listen for final training responses and reset the liveKey => streaming context to restart the stream
  useEffect(() => {
    if (!liveView) {
      // do nothing if live view is not open
      return;
    }

    if (!finalMsg) {
      // do nothing until the final training msg arrives
      return;
    }

    setLiveKey(Date.now());
  }, [liveView, finalMsg, setLiveKey]);

  const showBreaks = pitchTraining?.priority === BuildPriority.Breaks;
  const isRefreshingPitches = props.pitches.length === 0 || !pitchTraining;

  return (
    <ErrorBoundary identifier={props.identifier}>
      <Flex direction="column" gap={RADIX.FLEX.GAP.SM}>
        <Grid
          data-identifier="PTControls"
          columns="3"
          gap={RADIX.FLEX.GAP.SM}
          align="stretch"
        >
          {/* column 1 */}
          <Skeleton loading={isRefreshingPitches}>
            <Flex direction="column" gap="4">
              <Flex gap="2" align="center">
                <Heading size="3">{t('common.training-mode')}</Heading>

                <HeadingInfoPopover>
                  <Card className="no-background" style={{ padding: 0 }}>
                    <Flex direction="column" gap="3" m="5">
                      <Heading size="5">Training Modes</Heading>

                      <Box>
                        <Heading size="2">Quick</Heading>
                        <Text color={RADIX.COLOR.SECONDARY}>
                          Fastest way to train a pitch. Use when speed matters
                          more than precision.
                        </Text>
                      </Box>

                      <Box>
                        <Heading size="2">Precision</Heading>
                        <Text color={RADIX.COLOR.SECONDARY}>
                          Optimizes samples with smart criteria to make the
                          pitch as precise as possible. This is the recommended
                          method by Trajekt.
                        </Text>
                      </Box>

                      <Box>
                        <Heading size="2">Custom</Heading>
                        <Text color={RADIX.COLOR.SECONDARY}>
                          Use your own acceptance criteria to train pitches.
                        </Text>
                      </Box>

                      <Flex justify="end" style={{ width: '100%' }}>
                        <Button
                          onClick={() => {
                            window.open(
                              t('common.intercom-url') + HELP_URLS.TRAINING
                            );
                          }}
                        >
                          More Tips on Training <ExternalLinkIcon />
                        </Button>
                      </Flex>
                    </Flex>
                  </Card>
                </HeadingInfoPopover>
              </Flex>
              <PresetSelector />

              <PresetTrainingStatus
                isLastPitch={isLastPitch}
                onFailureRetrain={async () => {
                  // forces any previously collected shots to be ignored
                  setStartDate(undefined); // will trigger update matches
                  setLoadingUntil(addSeconds(new Date(), 2));
                  resetEverything();
                }}
                onFailureContinue={onFailureContinue}
                onSuccessChangePitch={changePitch}
                onSuccessFinish={() => {
                  props.onFinish?.();
                }}
              />
            </Flex>
          </Skeleton>

          {/* column 2 - plate */}
          <Skeleton loading={isRefreshingPitches}>
            <Flex
              direction="column"
              gap="4"
              gridColumn={showBreaks ? undefined : 'span 2'}
              style={{
                width: '100%',
                aspectRatio:
                  pitchTraining?.priority === BuildPriority.Breaks ? '1' : '2',
              }}
            >
              <Flex gap="2" align="center">
                <Heading size="3">{t('pd.plate-location')}</Heading>

                <Badge color={RADIX.COLOR.NEUTRAL}>Catcher's View</Badge>
              </Flex>

              <CardPadding0>
                {pitchTraining && (
                  <PresetTrainingPlateView
                    ptStep={
                      // * only change to/from complete to avoid redrawing the target layer constantly
                      step === PTStep.Complete && !showFailure
                        ? PTStep.Complete
                        : undefined
                    }
                    ptMode={lastTrainedMs?.preset?.mode}
                    newShotID={lastAimingShot?._id}
                    disabled
                  />
                )}
              </CardPadding0>
            </Flex>
          </Skeleton>

          {showBreaks && (
            <Skeleton loading={isRefreshingPitches}>
              <Flex
                direction="column"
                gap="4"
                style={{
                  width: '100%',
                  aspectRatio: '1',
                }}
              >
                <Flex gap="2" align="center">
                  <Heading size="3">{t('pd.breaks')}</Heading>
                </Flex>

                <PresetTrainingBreaks lastAimingShot={lastAimingShot} />
              </Flex>
            </Skeleton>
          )}
        </Grid>

        <Grid columns={liveView ? '3' : '1'} gap={RADIX.FLEX.GAP.SM}>
          {pitchTraining && (
            <Box gridColumn={liveView ? 'span 2' : undefined}>
              <Skeleton loading={isRefreshingPitches}>
                <TrainingDataTable
                  data-identifier="PTDataTable"
                  maxSamples={activeMode.spec.maxSamples}
                  priority={pitchTraining.priority ?? BuildPriority.Spins}
                  targetBS={pitchTraining.bs}
                  targetTraj={pitchTraining.traj}
                  targetBreaks={
                    pitchTraining.breaks ??
                    TrajHelper.getBreaks(pitchTraining.traj)
                  }
                  actualShots={trainingShots}
                  // update the rest of the UI given the change
                  afterArchive={() => setUpdateMatchesKey(Date.now())}
                />
              </Skeleton>
            </Box>
          )}

          {liveView && (
            <Card size="1">
              <StreamingProvider
                key={liveKey}
                machineID={machine.machineID}
                hwVersion={machine.hardware_version}
                defaultSource={CameraSource.STEREO_CHECK}
              >
                <Flex direction="column" gap="2">
                  <Flex gap="2" align="center">
                    <Box flexGrow="1">
                      <Heading size="3">{t('main.live-view')}</Heading>
                    </Box>
                    <IconButton
                      size="1"
                      color={RADIX.COLOR.NEUTRAL}
                      variant={RADIX.BUTTON.VARIANT.BORDERLESS}
                      className="btn-floating"
                      onClick={() => setLiveView(false)}
                    >
                      <Cross2Icon />
                    </IconButton>
                  </Flex>
                  <MachineCameraStreamViewer
                    machineID={machine.machineID}
                    loadingHeight="240px"
                    hideStreamFailed
                    hideRefreshRate
                    hideSource
                  />
                </Flex>
              </StreamingProvider>
            </Card>
          )}
        </Grid>
      </Flex>
    </ErrorBoundary>
  );
};
